import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Button,
  TextField,
  Stack,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Radio,
  Checkbox
} from '@mui/material';
import { MIconButton } from 'components/@material-extend';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import { RadioGroup } from '@mui/material';
import { FormControlLabel } from '@mui/material';
import { axios } from 'utils/axios';
import { DatePicker } from '@mui/lab';
import roundManageSearch from '@iconify/icons-ic/round-manage-search';
import roundFilterList from '@iconify/icons-ic/round-filter-list';

interface VehicleFiltersDrawerProps {
  open: boolean;
  onClose: () => void;
  filters: any;
  setFilters: React.Dispatch<React.SetStateAction<any>>;
  onApply: () => void;
  onClear: () => void;
}

const VehicleFiltersDrawer: React.FC<VehicleFiltersDrawerProps> = ({
  open,
  onClose,
  filters,
  setFilters,
  onApply,
  onClear
}) => {
  const [vehicleTypes, setVehicleType] = useState<any>([]);

  const fetchAllVehicleTypes = async () => {
    const response = await axios.get('/rest/api/vehicle-type');
    const allVehicleTypes = response.data.data.data.records || [];
    setVehicleType(
      allVehicleTypes?.map((el: any) => ({ value: el.VEHICLE_TYPE, id: el.VEHICLE_TYPE_ID }))
    );
  };

  useEffect(() => {
    fetchAllVehicleTypes();
  }, []);

  const data = [
    { id: 1, value: 'John Doe' },
    { id: 2, value: 'Jane Doe' },
    { id: 3, value: 'John Smith' },
    { id: 4, value: 'Jane Smith' }
  ];

  const handleMultiCheckboxChange =
    (field: string, value: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;

      setFilters((prevFilters: any) => {
        const currentValues = prevFilters[field]?.split(',').filter((v: any) => v.trim()) || [];
        const updatedValues = isChecked
          ? Array.from(new Set([...currentValues, value])) // Add the value if checked
          : currentValues.filter((v: any) => v !== value); // Remove the value if unchecked

        return {
          ...prevFilters,
          [field]: updatedValues.join(',')
        };
      });
    };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1, py: 2 }}
      >
        <Typography variant="subtitle1" sx={{ ml: 1 }}>
          Filters
        </Typography>
        <MIconButton onClick={onClose}>
          <Icon icon={closeFill} width={20} height={20} />
        </MIconButton>
      </Stack>

      <Divider />
      <Stack spacing={3} sx={{ p: 3 }}>
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Page Size
          </Typography>
          {/* Page Size */}
          <FormControl fullWidth>
            <InputLabel>Page Size</InputLabel>
            <Select
              value={filters.pageSize}
              onChange={(e) => setFilters({ ...filters, pageSize: e.target.value })}
              label="Page Size"
            >
              <MenuItem value="10">10</MenuItem>
              <MenuItem value="25">25</MenuItem>
              <MenuItem value="50">50</MenuItem>
            </Select>
          </FormControl>
        </div>
        {/* Start Date */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Date From
          </Typography>

          <DatePicker
            label="Date From"
            inputFormat="dd/MM/yyyy"
            value={filters.dateFrom || null}
            onChange={(newValue) => setFilters({ ...filters, dateFrom: newValue })}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </div>
        {/* End Date */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Date To
          </Typography>
          <DatePicker
            label="Date To"
            inputFormat="dd/MM/yyyy"
            value={filters.dateTo || null}
            onChange={(newValue) => setFilters({ ...filters, dateTo: newValue })}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </div>
        {/* Vehicle Type */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Vehicle Type
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Vehicle Type</InputLabel>
            <Select
              value={filters.VehicleType}
              onChange={(e) => setFilters({ ...filters, VehicleType: e.target.value })}
              label="Vehicle Type"
            >
              {vehicleTypes.map((item: any) => (
                <MenuItem key={item.id} value={item.value}>
                  {item.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        {/* Owner */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Owner
          </Typography>
          <Autocomplete
            options={data}
            getOptionLabel={(option) => option.value}
            value={data.find((item) => item.value === filters.Owner) || null}
            onChange={(e, newValue) => setFilters({ ...filters, Owner: newValue?.value || '' })}
            renderInput={(params) => <TextField {...params} label="Owner" fullWidth />}
          />
        </div>
        {/* Default Driver */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Default Driver
          </Typography>
          <Autocomplete
            options={data}
            getOptionLabel={(option) => option.value}
            value={data.find((item) => item.value === filters.DefaultDriver) || null}
            onChange={(e, newValue) =>
              setFilters({ ...filters, DefaultDriver: newValue?.value || '' })
            }
            renderInput={(params) => <TextField {...params} label="Driver" fullWidth />}
          />
        </div>
        {/* Client Default Name */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Client Default Name
          </Typography>
          <Autocomplete
            options={data}
            getOptionLabel={(option) => option.value}
            value={data.find((item) => item.value === filters.ClientDefaultName) || null}
            onChange={(e, newValue) =>
              setFilters({ ...filters, ClientDefaultName: newValue?.value || '' })
            }
            renderInput={(params) => (
              <TextField {...params} label="Client Default Name" fullWidth />
            )}
          />
        </div>
        {/* Haulier */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Haulier
          </Typography>
          <TextField
            fullWidth
            label="Haulier"
            value={filters.Haulier}
            onChange={(e) => setFilters({ ...filters, Haulier: e.target.value })}
          />
        </div>
        {/* Registration Plate */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Registration Plate
          </Typography>
          <TextField
            fullWidth
            label="Registration Plate No"
            value={filters.RegistrationPlate}
            onChange={(e) => setFilters({ ...filters, RegistrationPlate: e.target.value })}
          />
        </div>
        {/* Tare */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Tare
          </Typography>
          <FormControl component="fieldset">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={(filters.Tare || '').includes('1')}
                  onChange={handleMultiCheckboxChange('Tare', '1')}
                />
              }
              label="Yes"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={(filters.Tare || '').includes('0')}
                  onChange={handleMultiCheckboxChange('Tare', '0')}
                />
              }
              label="No"
            />
          </FormControl>
        </div>
      </Stack>
      {/* Buttons */}
      <Box sx={{ p: 3, pb: 0 }}>
        <Button
          fullWidth
          size="large"
          type="submit"
          color="primary"
          variant="outlined"
          onClick={onApply}
          startIcon={<Icon icon={roundManageSearch} />}
        >
          Apply Filters
        </Button>
      </Box>
      <Box sx={{ p: 3 }}>
        <Button
          fullWidth
          size="large"
          type="submit"
          color="secondary"
          variant="outlined"
          onClick={onClear}
          startIcon={<Icon icon={roundFilterList} />}
        >
          Clear
        </Button>
      </Box>
    </Drawer>
  );
};

export default VehicleFiltersDrawer;
